import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth is cookie-based (httpOnly access_token + refresh_token are set by the backend
// on /api/auth/login and cleared on /api/auth/logout). We never store the token
// in localStorage — that would expose it to XSS attacks.
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // `isAuthenticated` is just a hint to gate the /me check; the cookie is the
  // real source of truth and the backend rejects requests with no/expired cookie.
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (err) {
      // Logout is best-effort; the user still wants to be logged out client-side.
      console.error("AuthContext.logout: server call failed", err);
    }
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  logoutRef.current = logout;

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const headers = { ...(options.headers || {}), "Content-Type": "application/json" };
    // Normalise both `body` (fetch convention) and `data` (axios convention)
    // into axios' expected `data` field. Accepts either an object or a
    // JSON-encoded string — the axios JSON serializer is tolerant of both.
    const reqOptions = { ...options, headers, withCredentials: true };
    if (reqOptions.body !== undefined && reqOptions.data === undefined) {
      const b = reqOptions.body;
      reqOptions.data = typeof b === "string" ? JSON.parse(b) : b;
      delete reqOptions.body;
    }
    try {
      const res = await axios({ url, ...reqOptions });
      return { ok: true, status: res.status, data: res.data, json: async () => res.data };
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        // Try a single refresh round-trip (cookie carries the refresh_token).
        try {
          await axios.post(`${API_URL}/api/auth/refresh`);
          const retryRes = await axios({ url, ...reqOptions });
          return { ok: true, status: retryRes.status, data: retryRes.data, json: async () => retryRes.data };
        } catch (refreshErr) {
          console.error("AuthContext.fetchWithAuth: refresh failed", refreshErr);
        }
        logoutRef.current?.();
      }
      const errData = err.response?.data || { error: "Request failed" };
      return { ok: false, status: status || 500, data: errData, json: async () => errData };
    }
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      setUser({
        id: data.id, email: data.email, name: data.name, role: data.role,
        permissions: data.permissions,
        effective_permissions: data.effective_permissions || []
      });
      setIsAuthenticated(true);
      return data;
    } catch (err) {
      throw new Error(err.response?.data?.error || "Login failed");
    }
  };

  // True if the current user has the given permission key. Admin always passes
  // (the backend also enforces this — defence in depth).
  const hasPermission = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return (user.effective_permissions || []).includes(key);
  }, [user]);

  // Validate the session on mount by calling /me — the cookie auths us automatically.
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return undefined;
    }
    const source = axios.CancelToken.source();
    axios.get(`${API_URL}/api/auth/me`, { cancelToken: source.token })
      .then(res => {
        setUser(res.data);
        setIsAuthenticated(true);
      })
      .catch(err => {
        if (axios.isCancel(err)) return;
        // 401 means no/expired cookie — sit at the login screen.
        setUser(null);
        setIsAuthenticated(false);
      })
      .finally(() => {
        if (!source.token.reason) setLoading(false);
      });
    return () => source.cancel("cleanup");
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, fetchWithAuth, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
